import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircle2,
  ChevronRight,
  Gamepad2,
  Loader2,
  LogOut,
  Menu,
  MessageCircle,
  Plane,
  ShoppingCart,
  UserRound,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCommerce } from "@/contexts/CommerceContext";


const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [accountAction, setAccountAction] = useState("");
  const location = useLocation();
  const {
    account,
    accountLoading,
    cartCount,
    startFiveM,
    startDiscord,
    logout,
  } = useCommerce();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/products", label: "Products" },
    { href: "https://docs.aerocorescripts.com", label: "Documentation", external: true },
    { href: "/about", label: "About" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;
  const returnPath = `${location.pathname}${location.search}`;

  const connectAccount = async (provider) => {
    if (accountAction) return;
    setAccountAction(provider);
    try {
      if (provider === "fivem") await startFiveM(returnPath);
      if (provider === "discord") await startDiscord(returnPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || `${provider === "fivem" ? "FiveM" : "Discord"} login could not be started.`);
      setAccountAction("");
    }
  };

  const signOut = async () => {
    try {
      await logout();
      toast.success("Signed out successfully.");
    } catch {
      toast.error("Could not sign out. Please try again.");
    }
  };

  const accountLabel = account.fivem_connected || account.discord_connected ? "Account" : "Login";

  const AccountOptions = ({ mobile = false }) => (
    <div className={mobile ? "w-full max-w-xs space-y-3" : "space-y-1"}>
      <button
        type="button"
        onClick={() => connectAccount("fivem")}
        disabled={accountLoading || Boolean(accountAction) || account.fivem_connected}
        className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
          account.fivem_connected
            ? "border-emerald-400/20 bg-emerald-400/5"
            : "border-slate-800 bg-slate-900/60 hover:border-primary/40 hover:bg-primary/5"
        } disabled:cursor-default`}
      >
        <Gamepad2 className={`h-5 w-5 flex-shrink-0 ${account.fivem_connected ? "text-emerald-400" : "text-primary"}`} />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-bold uppercase tracking-wider text-white">FiveM</span>
          <span className="block truncate text-[11px] text-slate-400">
            {account.fivem_connected ? account.fivem_username : "Sign in with your Cfx.re account"}
          </span>
        </span>
        {accountAction === "fivem" ? (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        ) : account.fivem_connected ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500" />
        )}
      </button>

      <button
        type="button"
        onClick={() => connectAccount("discord")}
        disabled={accountLoading || Boolean(accountAction) || account.discord_connected || !account.fivem_connected}
        className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
          account.discord_connected
            ? "border-emerald-400/20 bg-emerald-400/5"
            : "border-[#5865f2]/25 bg-[#5865f2]/5 hover:border-[#5865f2]/50"
        } disabled:cursor-default disabled:opacity-55`}
        title={!account.fivem_connected ? "Connect FiveM first" : "Connect Discord"}
      >
        <MessageCircle className={`h-5 w-5 flex-shrink-0 ${account.discord_connected ? "text-emerald-400" : "text-[#8b95ff]"}`} />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-bold uppercase tracking-wider text-white">Discord</span>
          <span className="block truncate text-[11px] text-slate-400">
            {account.discord_connected
              ? account.discord_username
              : account.fivem_connected
                ? "Connect your Discord account"
                : "Connect FiveM first"}
          </span>
        </span>
        {accountAction === "discord" ? (
          <Loader2 className="h-4 w-4 animate-spin text-[#8b95ff]" />
        ) : account.discord_connected ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500" />
        )}
      </button>

      {(account.fivem_connected || account.discord_connected) && (
        <button
          type="button"
          onClick={signOut}
          className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs uppercase tracking-wider text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-300"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      )}
    </div>
  );

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`nav-fixed transition-all duration-300 ${isScrolled ? "py-3" : "py-4"}`}
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex items-center justify-between">
            <Link to="/" className="group flex items-center gap-3" data-testid="nav-logo">
              <div className="relative">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-primary/30 bg-primary/10 transition-colors group-hover:border-primary/60">
                  <Plane className="h-5 w-5 text-primary" />
                </div>
                <div className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-accent" />
              </div>
              <div>
                <div className="text-lg font-bold tracking-wider text-foreground">AEROCORE</div>
                <p className="-mt-1 text-[10px] tracking-[0.2em] text-muted-foreground">SCRIPTS</p>
              </div>
            </Link>

            <div className="hidden items-center gap-7 lg:flex">
              {navLinks.map((link) => {
                const className = `nav-link text-sm uppercase tracking-widest transition-colors ${
                  isActive(link.href) ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`;

                return link.external ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nav-link-documentation"
                    className={className}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`nav-link-${link.href.replace("/", "") || "home"}`}
                    className={className}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>

            <div className="hidden items-center gap-2 md:flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-lg border-slate-700 bg-slate-950/70 px-4 text-xs font-bold uppercase tracking-wider hover:border-primary/40 hover:bg-primary/5"
                    data-testid="customer-login-button"
                  >
                    <UserRound className="mr-2 h-4 w-4" /> {accountLabel}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 border-slate-700 bg-slate-950 p-2 shadow-2xl">
                  <DropdownMenuLabel className="px-2 py-2 text-xs uppercase tracking-[0.16em] text-slate-400">
                    Connect your accounts
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <div className="p-1"><AccountOptions /></div>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/cart">
                <Button
                  variant="outline"
                  className="relative rounded-lg border-slate-700 bg-slate-950/70 px-3 hover:border-primary/40 hover:bg-primary/5"
                  aria-label={`Basket with ${cartCount} items`}
                  data-testid="nav-cart-button"
                >
                  <ShoppingCart className="h-4 w-4" />
                  {cartCount > 0 && (
                    <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-slate-950">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </Link>

              <Link to="/products" className="hidden xl:block">
                <Button
                  data-testid="nav-cta-button"
                  className="rounded-lg border-l-4 border-accent bg-primary px-5 text-xs font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90 glow-blue-hover"
                >
                  Browse Products <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <button
              data-testid="mobile-menu-toggle"
              className="p-2 text-foreground md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle navigation"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu overflow-y-auto md:hidden"
          >
            <div className="flex min-h-full flex-col items-center justify-center gap-6 px-6 py-24">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                >
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="mobile-nav-link-documentation"
                      className="text-xl uppercase tracking-widest text-foreground hover:text-primary"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      data-testid={`mobile-nav-link-${link.href.replace("/", "") || "home"}`}
                      className={`text-xl uppercase tracking-widest ${
                        isActive(link.href) ? "text-primary" : "text-foreground hover:text-primary"
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </motion.div>
              ))}

              <div className="my-1 h-px w-full max-w-xs bg-slate-800" />
              <AccountOptions mobile />
              <Link to="/cart" className="w-full max-w-xs">
                <Button className="w-full rounded-lg border border-primary/30 bg-primary/10 py-6 font-bold uppercase tracking-wider text-primary">
                  <ShoppingCart className="mr-2 h-5 w-5" /> Basket ({cartCount})
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
